import './styles.scss';

import { DATE_INPUT_FORMAT, DATETIME_FE_FORMAT, MONTH_END, MONTH_START } from '@/constants';
import { Datetime, Validation, onEnter, printScreen, queryInput, saveFile } from '@/utils';
import { useMemo, useRef, useState } from 'react';
import VirtualTable, { DynamicCell, ExpandCell, VirtualTableRow, useVirtualTable } from '@/components/VirtualTable';

import Filters from '@/components/Filters';
import FormField from '@/components/FormField';
import { RouteProps } from 'react-router-dom';
import SelectAirlineIata from '@/components/SelectAirlineIata';
import SelectAirportGroup from '@/components/SelectAirportGroup';
import SelectClient from '@/components/SelectClient';
import SelectFcrReason from '@/components/SelectFcrReason';
import SidebarButton from '@/components/SidebarButton';
import { TripWithFcrInnerJoinFcrReporterEnum, TripWithFcrInnerJoinSearch } from '@/models/gen/graphql';
import { getClasses } from '@/utils/strings';
import { useSearchTripsWithFcr } from '@/api/services/fcrs/searchTripsWithFcr';
import { EnumDropdownMulti } from '@/components/EnumDropdown';
import { downloadTripWithFcrInnerJoinCsv } from '@/api/services/fcrs/downloadTripWithFcrInnerJoin';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PDFTable from '@/components/PDFTable/PDFTable';

type FcrFilters = {
  from: string;
  to: string;
  providerId: string[];
  airport: string[];
  airline: string[];
  reason: string[];
  flightNumber: string;
  reportedBy: TripWithFcrInnerJoinFcrReporterEnum[];
};
const initFcrFilters: FcrFilters = {
  from: MONTH_START,
  to: MONTH_END,
  providerId: [],
  airport: [],
  airline: [],
  reason: [],
  flightNumber: '',
  reportedBy: [],
};

const FcrReportersLabelMap: Record<TripWithFcrInnerJoinFcrReporterEnum, string> = {
  FLIGHT_ATTENDANT: 'Flight Attendant',
  PILOT: 'Pilot',
  UNKNOWN: 'Unknown',
};
const Fcrs = (_props: RouteProps) => {
  const [{ data, loading }, { refetch }] = useSearchTripsWithFcr();
  const { rows = [] } = data || {};
  const [filtersState, setFiltersState] = useState<FcrFilters>(initFcrFilters);

  const [state, setState] = useState({
    expanded: [],
  });
  const { expanded } = state;

  const { onExpand, filteredRows } = useVirtualTable(setState, {
    expanded,
    rows,
  });

  const lastQuery = useRef(null);

  const handleCSVDownload = async (): Promise<void> => {
    const res = await downloadTripWithFcrInnerJoinCsv([
      {
        from: filtersState.from,
        to: filtersState.to,
        airportCode: filtersState.airport,
        fcrReason: filtersState.reason,
        fcrReporter: filtersState.reportedBy,
        flightNumber: filtersState.flightNumber,
        providerId: filtersState.providerId,
        servicerIataAirlineCode: filtersState.airline,
      },
    ]);
    if (!res) return;
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    saveFile(url, `Complaints-${new Datetime().format(DATE_INPUT_FORMAT)}.csv`);
  };

  const onSubmit = async (filters: FcrFilters): Promise<void> => {
    const { from, to, providerId, airport, airline, reason, flightNumber, reportedBy } = filters || {};
    setFiltersState(filters);
    const query: TripWithFcrInnerJoinSearch = {
      scheduledUtc: queryInput.date([from, to]),
      providerId: providerId?.length ? queryInput(providerId) : null,
      airportCode: airport?.length ? queryInput(airport) : null,
      servicerIataAirlineCode: airline?.length ? queryInput(airline) : null,
      fcrReason: reason?.length ? queryInput(reason) : null,
      flightNumber: flightNumber ? queryInput(flightNumber) : null,
      fcrReporter: reportedBy?.length ? queryInput(reportedBy) : null,
    };
    await refetch([query]);
    lastQuery.current = query;
  };

  const tableHeaders = {
    scheduled: 'Trip Date',
    airportCode: 'Airport',
    provider: { displayName: 'Client' },
    flightNumber: 'Flight Number',
    fcrReason: 'Reason',
    fcrReporter: 'Reported By',
  };

  const mappedRows = useMemo(() => {
    return (filteredRows || []).map((row) => ({
      ...row,
      children: [
        { child: true, scheduled: 'COMPLAINT DESCRIPTION', airportCode: row?.fcrDescription || '--' },
        { child: true, scheduled: 'ROOT CAUSE', airportCode: row?.fcrCause || '--' },
      ],
    }));
  }, [filteredRows]);

  return (
    <div className="Fcrs page-container">
      <Filters
        name="fcrs"
        value={initFcrFilters}
        onSubmit={onSubmit}
        primary={({ values, onChange }) => {
          const { from, to, providerId, airport, airline, reason, flightNumber, reportedBy } = values;
          return (
            <>
              <FormField name="dates" type="daterange" value={[from, to]} onChange={onChange.dateRange('from', 'to')} condensed />
              <FormField
                name="providerId"
                onChange={onChange}
                value={providerId}
                placeholder="Client"
                searchable
                options={{ input: { as: SelectClient } }}
                condensed
              />
              <FormField
                name="airport"
                onChange={onChange}
                value={airport}
                placeholder="Airport"
                multiple
                searchable
                options={{ input: { as: SelectAirportGroup } }}
                condensed
              />
              <FormField
                name="airline"
                onChange={onChange}
                value={airline}
                placeholder="Airline"
                multiple
                searchable
                options={{ input: { as: SelectAirlineIata } }}
                condensed
              />
              <FormField
                name="reason"
                onChange={onChange}
                value={reason}
                placeholder="Reason"
                multiple
                searchable
                options={{ input: { as: SelectFcrReason } }}
                condensed
              />
              <FormField
                name="flightNumber"
                placeholder="Flight"
                value={Validation.isNumber(flightNumber) ? flightNumber : initFcrFilters?.flightNumber}
                onChange={onChange.int}
                onKeyDown={onEnter((): Promise<void> => onSubmit(values || initFcrFilters))}
                maxLength={4}
                condensed
              />
              <EnumDropdownMulti
                name="reportedBy"
                value={reportedBy}
                onChange={(value) => onChange({ target: { name: 'reportedBy', value } })}
                enum={TripWithFcrInnerJoinFcrReporterEnum}
                placeholder="Reported By"
                multiple
                options={{ locale: { 'Select...': 'Reported By', FlightAttendant: 'Flight Attendant' } }}
              />
            </>
          );
        }}
        submitOnMount
      />
      <VirtualTable
        name="FcrTable"
        className="condensed"
        loading={loading}
        data={mappedRows}
        expanded={expanded}
        header={tableHeaders}
        rowRenderer={({ index, data: { _type, ...data } = {}, context = {} }: { index: any; data: any; context: any }): JSX.Element => (
          <VirtualTableRow
            context={{
              ...context,
              rowType: _type,
              data,
              index,
              expanded: expanded.includes(data?.id || index),
            }}
            className={getClasses(
              expanded.includes(data?.id || index) ? 'expanded' : '',
              `${data?.id || index}`.includes('_') ? 'nested' : '',
              !data?.children?.length ? 'no-children' : ''
            )}
            onDoubleClick={(data?.children || []).length ? (): void => onExpand(data?.id || index) : undefined}
          >
            <ExpandCell className="alternate" onClick={onExpand} />
            <DynamicCell
              selector="scheduled"
              placeholder="--"
              className="text-center"
              width="calc(100% / 5)"
              render={({ value, data }: { value: string; data: any }): string =>
                data?.child ? value : new Datetime(value).format(DATETIME_FE_FORMAT)
              }
            />
            <DynamicCell
              selector="airportCode"
              placeholder={data?.child ? ' ' : '--'}
              className={`${data?.child ? 'text-start' : 'text-center'} alternate`}
              width={data?.child ? `calc(400% / 5)` : `calc(100% / 5)`}
            />
            {!data?.child && (
              <>
                <DynamicCell selector="provider.displayName" placeholder="--" className="text-center" width="calc(100% / 5)" />
                <DynamicCell
                  selector="flightNumber"
                  placeholder="--"
                  className="text-center alternate"
                  width="calc(100% / 5)"
                  render={({ data }: { data: any }): string =>
                    (data?.flightNumber && data.flightNumber > 0 ? data.flightNumber : data?.loopName) ?? '--'
                  }
                />
                <DynamicCell selector="fcrReason" placeholder="--" className="text-center" width="calc(100% / 5)" />
              </>
            )}
            <DynamicCell
              selector="fcrReporter"
              placeholder="--"
              className={`${data?.child ? 'text-start' : 'text-center'} alternate`}
              render={({ value }: { value: TripWithFcrInnerJoinFcrReporterEnum }): string =>
                data?.child ? value : FcrReportersLabelMap[value] || value || '--'
              }
              width={'calc(100% / 5)'}
            />
          </VirtualTableRow>
        )}
      />
      <SidebarButton
        xAlign={SidebarButton.xAlign.RIGHT}
        yAlign={SidebarButton.yAlign.TOP}
        onClick={handleCSVDownload}
        tooltip="Export to CSV"
      >
        <i className="sv sv-excel" />
      </SidebarButton>
      <SidebarButton xAlign={SidebarButton.xAlign.RIGHT} yAlign={SidebarButton.yAlign.TOP} onClick={printScreen} tooltip="Print">
        <i className="sv sv-print" />
      </SidebarButton>
      {mappedRows.length > 0 && (
        <SidebarButton name="PDF" tooltip="Download as PDF">
          <PDFDownloadLink
            className="text-decoration-none text-white"
            document={<PDFTable name="Complaints" headers={tableHeaders} rows={mappedRows} filters={filtersState} />}
            fileName={`complaints-${new Date().toISOString().split('T')[0]}.pdf`}
            style={{ color: '#fffff' }}
          >
            <i className="sv sv-download2" />
          </PDFDownloadLink>
        </SidebarButton>
      )}
    </div>
  );
};
export default Fcrs;
